import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';
import type { IApiMessage } from '@/store/types';
import type { NonPayloadRequest, PayloadRequest, StandardRequest } from '@/store';

import { KnownErrors } from '@/store/enums';

const logError = (error: AxiosError): void => {
  if (axios.isCancel(error)) {
    console.warn(error.message);
  } else {
    const message = error.response ? `${error.response.status} ${error.response.statusText}` : error.message || error;
    console.error(message);
  }
};

const _isApiMessages = (object: any): object is AxiosResponse<IApiMessage[]> => {
  return object.status === 400 && Array.isArray(object.data) && object.data.length;
};

const parseError = (response: unknown): string => {
  if (_isApiMessages(response)) {
    return response.data[0].message;
  }

  const { status, statusText } = response as AxiosResponse;
  return `${status} ${statusText}`;
};

const _isPayloadAction = <Return, Payload>(action: unknown): action is PayloadRequest<Return, Payload> => {
  return (action as NonPayloadRequest<Return>).length === 2;
};

const _isNonPayloadAction = <Return>(action: unknown): action is NonPayloadRequest<Return> => {
  return (action as NonPayloadRequest<Return>).length === 1;
};

const makeCancellable = <Return, Payload = void>(
  apiAction: StandardRequest<Return, Payload>,
  signal: AbortSignal,
): StandardRequest<Return, Payload> => {
  const source = axios.CancelToken.source();
  signal.addEventListener('abort', () => {
    source.cancel(`${apiAction.name} cancelled`);
  });
  const getConfig = (config?: AxiosRequestConfig) => ({ cancelToken: source.token, ...config });
  if (_isNonPayloadAction(apiAction)) {
    return ((config: AxiosRequestConfig | undefined = undefined) =>
      apiAction(getConfig(config))) as NonPayloadRequest<Return>;
  }
  if (_isPayloadAction(apiAction)) {
    return ((payload: Payload, config: AxiosRequestConfig | undefined = undefined) =>
      apiAction(payload, getConfig(config))) as StandardRequest<Return, Payload>;
  }
  throw 'Unexpected apiAction type';
};

const isKnownError = <T>(data: KnownErrors | T): data is KnownErrors =>
  Object.values(KnownErrors).includes(data as KnownErrors);

const parseSearchParams = (params: Record<string, string | number | boolean | undefined>): string =>
  Object.entries(params)
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

const parseQueryParamsUrl = (url: string, params: Record<string, string | number | boolean | undefined>): string => {
  const paramsString = parseSearchParams(params);
  if (paramsString) return `${url}?${paramsString}`;
  return url;
};

const convertToFormData = (data: any, form: FormData | null = null, parentKey = ''): FormData => {
  const formData = form || new FormData();

  if (
    data &&
    typeof data === 'object' &&
    !(data instanceof Date) &&
    !(data instanceof File) &&
    !(Array.isArray(data) && !data.length)
  ) {
    Object.keys(data).forEach(key => {
      convertToFormData(data[key], formData, parentKey ? `${parentKey}[${key}]` : key);
    });
  } else {
    const value = data === null ? '' : data;

    formData.append(parentKey, value);
  }

  return formData;
};

export {
  logError,
  parseError,
  makeCancellable,
  isKnownError,
  parseQueryParamsUrl,
  parseSearchParams,
  convertToFormData,
};
